import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { ContactLabelSelector } from '../components/ContactLabelSelector';

type Automation = {
  id: string;
  name: string;
  enabled: boolean;
  webhookUrl: string;
  sessionNames: string[];
  messageType: string;
  messageContent: any;
  randomDelay: number;
  randomDelayMax: number | null;
  scheduleStartDay: number | null;
  scheduleEndDay: number | null;
  scheduleStartHour: number | null;
  scheduleEndHour: number | null;
  categoryId: string | null;
  contactTags?: string[];
  category?: { id: string; nome: string } | null;
  lateAccountId?: string | null;
  lateTemplateName?: string | null;
  lateTemplateLang?: string | null;
};

const DAYS = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
const MESSAGE_TYPES = [
  { value: 'text', label: '💬 Texto' },
  { value: 'image', label: '🖼️ Imagem' },
  { value: 'video', label: '🎬 Vídeo' },
  { value: 'audio', label: '🎵 Áudio' },
  { value: 'document', label: '📄 Arquivo' },
  { value: 'openai', label: '🤖 OpenAI' },
  { value: 'wait', label: '⏱️ Espera' },
];
const WEBHOOK_VARIABLES = [
  { key: 'nome', label: 'Nome' },
  { key: 'telefone', label: 'Telefone' },
  { key: 'itens', label: 'Itens' },
  { key: 'numeroPedido', label: 'Numero pedido' },
  { key: 'numeroNotaFiscal', label: 'Numero NF' },
  { key: 'valorPedido', label: 'Valor pedido' },
  { key: 'statusPedido', label: 'Status pedido' },
  { key: 'produto', label: 'Produto' },
  { key: 'codigo', label: 'Codigo' },
  { key: 'email', label: 'Email' },
  { key: 'categoria', label: 'Categoria' },
];

function defaultContent(type: string) {
  if (type === 'text') return { text: '' };
  if (type === 'document') return { url: '', caption: '', fileName: '' };
  if (type === 'openai') return { model: '', system: '', user: '' };
  if (type === 'wait') return { waitTime: 30 };
  return { url: '', caption: '' };
}

function bodyText(template: any) {
  return template?.components?.find((item: any) => item.type === 'BODY' || item.type === 'body')?.text || '';
}

function extractVars(text: string) {
  const seen = new Set<string>();
  const vars: string[] = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/gi;
  let match;
  while ((match = re.exec(text || '')) !== null) {
    const key = match[1].trim().toLowerCase();
    if (!/^\d+$/.test(key) && !seen.has(key)) {
      seen.add(key);
      vars.push(key);
    }
  }
  return vars;
}

export function WebhookAutomationPage() {
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = { 'Content-Type': 'application/json', ...options.headers };
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    if (selectedTenantId) (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    return fetch(url, { ...options, headers });
  }, []);

  const [automations, setAutomations] = useState<Automation[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [funnels, setFunnels] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showDoc, setShowDoc] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [useOfficial, setUseOfficial] = useState(false);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [sessionNames, setSessionNames] = useState<string[]>([]);
  const [sequence, setSequence] = useState([{ type: 'text', content: { text: '' } as any }]);
  const [randomDelay, setRandomDelay] = useState(0);
  const [randomDelayMax, setRandomDelayMax] = useState<number | null>(30);
  const [scheduleStartDay, setScheduleStartDay] = useState<number | null>(null);
  const [scheduleEndDay, setScheduleEndDay] = useState<number | null>(null);
  const [scheduleStartHour, setScheduleStartHour] = useState<number | null>(null);
  const [scheduleEndHour, setScheduleEndHour] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [lateAccountId, setLateAccountId] = useState('');
  const [lateTemplateName, setLateTemplateName] = useState('');
  const [lateTemplateLang, setLateTemplateLang] = useState('pt_BR');
  const [templateText, setTemplateText] = useState('');
  const [templateVarOrder, setTemplateVarOrder] = useState<string[]>([]);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  const loadAll = useCallback(async () => {
    const [autoRes, sessionsRes, catRes, funnelRes, accRes] = await Promise.all([
      authenticatedFetch('/api/webhook-automations'),
      authenticatedFetch('/api/campaigns/sessions'),
      authenticatedFetch('/api/campaigns/tags'),
      authenticatedFetch('/api/funis'),
      authenticatedFetch('/api/whatsapp-templates/accounts'),
    ]);
    setAutomations((await autoRes.json()).automations || []);
    setSessions(await sessionsRes.json());
    setCategories(await catRes.json());
    const funnelData = await funnelRes.json();
    setFunnels(Array.isArray(funnelData) ? funnelData : funnelData.funnels || []);
    const accData = await accRes.json();
    setAccounts(accData.accounts || accData.data || accData || []);
  }, [authenticatedFetch]);

  const loadTemplates = useCallback(async (accountId: string) => {
    if (!accountId) return setTemplates([]);
    const res = await authenticatedFetch(`/api/whatsapp-templates/templates?accountId=${accountId}`);
    const data = await res.json();
    setTemplates(data.templates || data.data || data || []);
  }, [authenticatedFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!categoryDropdownOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [categoryDropdownOpen]);

  const funnelMap = useMemo(() => {
    const map = new Map<string, any>();
    funnels.forEach((funnel) => map.set(funnel.id, funnel));
    return map;
  }, [funnels]);

  const selectedCategory = useMemo(() => categories.find((category) => category.id === categoryId) || null, [categories, categoryId]);
  const selectedFunnel = selectedCategory?.funnelId ? funnelMap.get(selectedCategory.funnelId) : selectedCategory?.funnel || null;

  const groupedCategories = useMemo(() => {
    const term = categorySearch.trim().toLowerCase();
    const matches = (category: any, funnel?: any) => {
      if (!term) return true;
      return String(category.nome || '').toLowerCase().includes(term) || String(funnel?.nome || category.funnel?.nome || '').toLowerCase().includes(term);
    };

    const byFunnel: Array<{ funnel: any; items: any[] }> = [];
    funnels.forEach((funnel) => {
      const items = categories.filter((category) => category.funnelId === funnel.id && matches(category, funnel));
      if (items.length > 0) byFunnel.push({ funnel, items });
    });
    const noFunnel = categories.filter((category) => !category.funnelId && matches(category));
    return { byFunnel, noFunnel };
  }, [categories, funnels, categorySearch]);

  const hasCategoryResults = groupedCategories.byFunnel.length > 0 || groupedCategories.noFunnel.length > 0;

  const selectCategory = (id: string) => {
    setCategoryId(id);
    setCategoryDropdownOpen(false);
    setCategorySearch('');
  };

  const reset = () => {
    setEditingId(null);
    setShowForm(false);
    setUseOfficial(false);
    setName('');
    setEnabled(false);
    setSessionNames([]);
    setSequence([{ type: 'text', content: { text: '' } }]);
    setRandomDelay(0);
    setRandomDelayMax(30);
    setScheduleStartDay(null);
    setScheduleEndDay(null);
    setScheduleStartHour(null);
    setScheduleEndHour(null);
    setCategoryId('');
    setContactTags([]);
    setCategorySearch('');
    setCategoryDropdownOpen(false);
    setLateAccountId('');
    setLateTemplateName('');
    setLateTemplateLang('pt_BR');
    setTemplateText('');
    setTemplateVarOrder([]);
  };

  const edit = (item: Automation) => {
    setEditingId(item.id);
    setShowForm(true);
    setName(item.name);
    setEnabled(item.enabled);
    setRandomDelay(item.randomDelay || 0);
    setRandomDelayMax(item.randomDelayMax);
    setScheduleStartDay(item.scheduleStartDay);
    setScheduleEndDay(item.scheduleEndDay);
    setScheduleStartHour(item.scheduleStartHour);
    setScheduleEndHour(item.scheduleEndHour);
    setCategoryId(item.categoryId || '');
    setContactTags(item.contactTags || []);
    if (item.messageType === 'whatsapp_template') {
      setUseOfficial(true);
      setLateAccountId(item.lateAccountId || '');
      setLateTemplateName(item.lateTemplateName || '');
      setLateTemplateLang(item.lateTemplateLang || 'pt_BR');
      setTemplateText(item.messageContent?.text || '');
      setTemplateVarOrder(item.messageContent?.variableOrder || []);
      if (item.lateAccountId) loadTemplates(item.lateAccountId);
    } else {
      setUseOfficial(false);
      setSessionNames(item.sessionNames || []);
      setSequence(item.messageType === 'sequence' ? item.messageContent?.sequence || [] : [{ type: item.messageType, content: item.messageContent }]);
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error('Nome obrigatorio');
    if (useOfficial && (!lateAccountId || !lateTemplateName)) return toast.error('Selecione conta oficial e template');
    if (!useOfficial && sessionNames.length === 0) return toast.error('Selecione uma conexao WhatsApp');
    const isSequence = !useOfficial && sequence.length > 1;
    const body = {
      name,
      enabled,
      sessionNames: useOfficial ? [] : sessionNames,
      messageType: useOfficial ? 'whatsapp_template' : isSequence ? 'sequence' : sequence[0].type,
      messageContent: useOfficial ? { text: templateText, variableOrder: templateVarOrder } : isSequence ? { sequence } : sequence[0].content,
      randomDelay,
      randomDelayMax,
      scheduleStartDay,
      scheduleEndDay,
      scheduleStartHour,
      scheduleEndHour,
      categoryId: categoryId || null,
      contactTags,
      lateAccountId: useOfficial ? lateAccountId : null,
      lateTemplateName: useOfficial ? lateTemplateName : null,
      lateTemplateLang: useOfficial ? lateTemplateLang : null,
    };
    const res = await authenticatedFetch(editingId ? `/api/webhook-automations/${editingId}` : '/api/webhook-automations', {
      method: editingId ? 'PUT' : 'POST',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data.error || 'Erro ao salvar');
    toast.success(editingId ? 'Automacao atualizada' : 'Automacao criada');
    reset();
    loadAll();
  };

  const updateSequence = (index: number, content: any) => {
    setSequence((prev) => prev.map((item, i) => i === index ? { ...item, content: { ...item.content, ...content } } : item));
  };

  const uploadMedia = async (index: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('auth_token');
      const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
      const headers: HeadersInit = {};
      if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      if (selectedTenantId) (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
      const response = await fetch('/api/media/upload', { method: 'POST', body: formData, headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Erro ao subir arquivo');
      updateSequence(index, { url: data.fileUrl, fileName: data.originalName || file.name });
      toast.success('Arquivo enviado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao subir arquivo');
    }
  };

  const appendVariable = (index: number, field: string, variable: string) => {
    const item = sequence[index];
    const current = item?.content?.[field] || '';
    updateSequence(index, { [field]: `${current}${current ? ' ' : ''}{{${variable}}}` });
  };

  const variableButtons = (index: number, field: string) => (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {WEBHOOK_VARIABLES.map((variable) => (
        <button
          key={`${field}-${variable.key}`}
          type="button"
          onClick={() => appendVariable(index, field, variable.key)}
          className="px-2 py-1 text-[11px] rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
        >
          {`{{${variable.key}}}`}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <Header
        title="Automações com webhook"
        subtitle="Dispare mensagens quando um sistema externo enviar um POST para o webhook"
      />
      <div className="p-6">
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Regras de disparo por webhook</h2>
              <p className="text-sm text-gray-500">Use variaveis como {'{{nome}}'}, {'{{telefone}}'} e qualquer campo enviado em variables.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowDoc(true)} className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Ver doc do webhook</button>
              {!showForm && <button onClick={() => { reset(); setShowForm(true); }} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium">+ Nova automação</button>}
            </div>
          </div>

          <div className="space-y-3 mb-5">
            {automations.map((item) => (
              <div key={item.id} className={`border rounded-lg p-4 ${item.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <button onClick={async () => { await authenticatedFetch(`/api/webhook-automations/${item.id}`, { method: 'PUT', body: JSON.stringify({ enabled: !item.enabled }) }); loadAll(); }} className={`h-6 w-11 rounded-full ${item.enabled ? 'bg-green-500' : 'bg-gray-300'}`}><span className={`block h-4 w-4 rounded-full bg-white transition-transform ${item.enabled ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                      <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.messageType === 'whatsapp_template' ? `API Oficial | Template: ${item.lateTemplateName}` : `WhatsApp nao oficial | ${item.sessionNames.join(', ')}`}{item.category ? ` | Categoria: ${item.category.nome}` : ''}</p>
                    <div className="flex gap-2 mt-2">
                      <input readOnly value={item.webhookUrl} className="w-full max-w-2xl text-xs px-2 py-1 border rounded bg-white" />
                      <button onClick={() => { navigator.clipboard.writeText(item.webhookUrl); toast.success('URL copiada'); }} className="px-2 py-1 text-xs rounded border bg-white">Copiar</button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => edit(item)} className="px-3 py-1 text-xs rounded bg-indigo-50 text-indigo-700 border border-indigo-200">Editar</button>
                    <button onClick={async () => { if (!confirm('Excluir automacao?')) return; await authenticatedFetch(`/api/webhook-automations/${item.id}`, { method: 'DELETE' }); loadAll(); }} className="px-3 py-1 text-xs rounded bg-red-50 text-red-700 border border-red-200">Excluir</button>
                  </div>
                </div>
              </div>
            ))}
            {automations.length === 0 && !showForm && <div className="py-8 text-center text-sm text-gray-400">Nenhuma automacao configurada.</div>}
          </div>

          {showForm && (
            <div className="border border-indigo-200 rounded-lg p-5 bg-indigo-50/30 space-y-4">
              <h3 className="text-sm font-semibold text-gray-800">{editingId ? 'Editar automacao' : 'Nova automação'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-xs font-medium text-gray-600">Nome<input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 w-full px-3 py-2 border rounded-md text-sm" placeholder="Ex: Lead vindo do site" /></label>
                <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> Habilitado</label>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setUseOfficial(false)} className={`px-3 py-2 text-xs rounded border ${!useOfficial ? 'bg-indigo-600 text-white' : 'bg-white'}`}>WhatsApp nao oficial</button>
                <button onClick={() => setUseOfficial(true)} className={`px-3 py-2 text-xs rounded border ${useOfficial ? 'bg-green-600 text-white' : 'bg-white'}`}>API Oficial WhatsApp</button>
              </div>

              {!useOfficial ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Conexoes WhatsApp</label>
                  <div className="flex flex-wrap gap-2">{sessions.map((session) => <button key={session.name} onClick={() => setSessionNames((prev) => prev.includes(session.name) ? prev.filter((item) => item !== session.name) : [...prev, session.name])} className={`px-3 py-1.5 text-xs rounded-md border ${sessionNames.includes(session.name) ? 'bg-indigo-600 text-white' : 'bg-white'}`}>{session.displayName || session.name}</button>)}</div>
                </div>
              ) : (
                <div className="p-3 border border-green-200 bg-green-50 rounded-lg space-y-3">
                  <select value={lateAccountId} onChange={(event) => { setLateAccountId(event.target.value); setLateTemplateName(''); loadTemplates(event.target.value); }} className="w-full px-3 py-2 border rounded-md text-sm"><option value="">Selecione a conta oficial...</option>{accounts.map((account) => <option key={account.id} value={account.platformAccountId || account.id}>{account.accountName}</option>)}</select>
                  <select value={lateTemplateName} onChange={(event) => { const selected = templates.find((template) => template.name === event.target.value); const text = bodyText(selected); setLateTemplateName(event.target.value); setLateTemplateLang(selected?.language || 'pt_BR'); setTemplateText(text); setTemplateVarOrder(selected?.variableOrder || extractVars(text)); }} className="w-full px-3 py-2 border rounded-md text-sm"><option value="">Selecione o template...</option>{templates.map((template) => <option key={template.id || template.name} value={template.name}>{template.name} ({template.language}) {template.status}</option>)}</select>
                  {lateTemplateName && <textarea readOnly value={templateText} rows={4} className="w-full px-3 py-2 border rounded-md text-sm bg-white" />}
                  {templateVarOrder.length > 0 && <p className="text-xs text-green-700">Variaveis: {templateVarOrder.map((key, index) => `{{${index + 1}}}=${key}`).join(', ')}</p>}
                </div>
              )}

              {!useOfficial && (
                <div className="space-y-3">
                  <div className="flex justify-between"><label className="text-xs font-medium text-gray-600">Mensagens</label><button onClick={() => setSequence([...sequence, { type: 'text', content: { text: '' } }])} className="text-xs text-indigo-700">+ Adicionar etapa</button></div>
                  {sequence.map((item, index) => (
                    <div key={index} className="p-3 bg-white border rounded-lg">
                      <div className="flex gap-2 mb-2"><select value={item.type} onChange={(event) => setSequence((prev) => prev.map((seq, i) => i === index ? { type: event.target.value, content: defaultContent(event.target.value) } : seq))} className="px-2 py-1 border rounded text-sm">{MESSAGE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select>{sequence.length > 1 && <button onClick={() => setSequence(sequence.filter((_, i) => i !== index))} className="text-xs text-red-600">Remover</button>}</div>
                      {item.type === 'text' && <><textarea value={item.content.text || ''} onChange={(event) => updateSequence(index, { text: event.target.value })} rows={3} className="w-full px-3 py-2 border rounded text-sm" placeholder="Mensagem com {{nome}}" />{variableButtons(index, 'text')}</>}
                      {['image', 'video', 'audio'].includes(item.type) && <><div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mb-2"><input value={item.content.url || ''} onChange={(event) => updateSequence(index, { url: event.target.value })} className="w-full px-3 py-2 border rounded text-sm" placeholder="URL da midia ou suba um arquivo" /><label className="px-3 py-2 text-xs rounded border bg-slate-50 hover:bg-slate-100 cursor-pointer text-center">Subir arquivo<input type="file" accept={item.type === 'image' ? 'image/*' : item.type === 'video' ? 'video/*' : 'audio/*'} className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadMedia(index, file); event.currentTarget.value = ''; }} /></label></div><input value={item.content.caption || ''} onChange={(event) => updateSequence(index, { caption: event.target.value })} className="w-full px-3 py-2 border rounded text-sm" placeholder="Legenda" />{variableButtons(index, 'caption')}</>}
                      {item.type === 'document' && <><div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mb-2"><input value={item.content.url || ''} onChange={(event) => updateSequence(index, { url: event.target.value })} className="w-full px-3 py-2 border rounded text-sm" placeholder="URL do arquivo ou suba um arquivo" /><label className="px-3 py-2 text-xs rounded border bg-slate-50 hover:bg-slate-100 cursor-pointer text-center">Subir arquivo<input type="file" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadMedia(index, file); event.currentTarget.value = ''; }} /></label></div><input value={item.content.fileName || ''} onChange={(event) => updateSequence(index, { fileName: event.target.value })} className="w-full px-3 py-2 border rounded text-sm mb-2" placeholder="Nome do arquivo" /><input value={item.content.caption || ''} onChange={(event) => updateSequence(index, { caption: event.target.value })} className="w-full px-3 py-2 border rounded text-sm" placeholder="Legenda" />{variableButtons(index, 'caption')}</>}
                      {item.type === 'wait' && <input type="number" value={item.content.waitTime || 30} onChange={(event) => updateSequence(index, { waitTime: Number(event.target.value) })} className="px-3 py-2 border rounded text-sm" />}
                      {item.type === 'openai' && <><input value={item.content.model || ''} onChange={(event) => updateSequence(index, { model: event.target.value })} className="w-full px-3 py-2 border rounded text-sm mb-2" placeholder="Modelo" /><textarea value={item.content.system || ''} onChange={(event) => updateSequence(index, { system: event.target.value })} rows={2} className="w-full px-3 py-2 border rounded text-sm mb-2" placeholder="System" /><textarea value={item.content.user || ''} onChange={(event) => updateSequence(index, { user: event.target.value })} rows={2} className="w-full px-3 py-2 border rounded text-sm" placeholder="User" />{variableButtons(index, 'user')}</>}
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="text-xs font-medium text-gray-600">Delay min. (s)<input type="number" value={randomDelay} onChange={(event) => setRandomDelay(Number(event.target.value))} className="mt-1 w-full px-3 py-2 border rounded text-sm" /></label>
                <label className="text-xs font-medium text-gray-600">Delay max. (s)<input type="number" value={randomDelayMax ?? ''} onChange={(event) => setRandomDelayMax(event.target.value ? Number(event.target.value) : null)} className="mt-1 w-full px-3 py-2 border rounded text-sm" /></label>
                <div className="text-xs font-medium text-gray-600">
                  Salvar contato na categoria
                  <div ref={categoryDropdownRef} className="relative mt-1">
                    <button type="button" onClick={() => setCategoryDropdownOpen((open) => !open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 border rounded bg-white text-left text-sm font-normal">
                      {selectedCategory ? (
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: selectedCategory.cor || '#6b7280' }} />
                          <span className="truncate font-medium text-gray-800">{selectedCategory.nome}</span>
                          {selectedFunnel && <span className="text-xs text-gray-400 truncate">- {selectedFunnel.nome}</span>}
                        </span>
                      ) : <span className="text-gray-400">Nao salvar / manter atual</span>}
                      <svg className={`h-4 w-4 text-gray-400 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    </button>
                    {categoryDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                        <div className="p-2 border-b border-gray-100 space-y-2">
                          <input autoFocus type="text" value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} placeholder="Buscar categoria ou funil..." className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                          <button type="button" onClick={() => selectCategory('')} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-50 text-gray-500">Nao salvar / manter atual</button>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {!hasCategoryResults && <p className="px-3 py-3 text-sm text-gray-400 text-center">Nenhum resultado</p>}
                          {groupedCategories.byFunnel.map(({ funnel, items }) => (
                            <div key={funnel.id}>
                              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                                <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: funnel.cor || '#6b7280' }} />
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{funnel.nome}</span>
                              </div>
                              {items.map((cat) => (
                                <button key={cat.id} type="button" onClick={() => selectCategory(cat.id)} className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-indigo-50 transition-colors ${categoryId === cat.id ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-gray-700'}`}>
                                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: cat.cor || '#6b7280' }} />
                                  <span className="truncate">{cat.nome}</span>
                                </button>
                              ))}
                            </div>
                          ))}
                          {groupedCategories.noFunnel.length > 0 && (
                            <div>
                              <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sem funil</span></div>
                              {groupedCategories.noFunnel.map((cat) => (
                                <button key={cat.id} type="button" onClick={() => selectCategory(cat.id)} className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-indigo-50 transition-colors ${categoryId === cat.id ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-gray-700'}`}>
                                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: cat.cor || '#6b7280' }} />
                                  <span className="truncate">{cat.nome}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <ContactLabelSelector value={contactTags} onChange={setContactTags} label="Etiquetas adicionadas ao contato recebido pelo webhook" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <select value={scheduleStartDay ?? ''} onChange={(event) => setScheduleStartDay(event.target.value === '' ? null : Number(event.target.value))} className="px-3 py-2 border rounded text-sm"><option value="">Dia inicial</option>{DAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}</select>
                <select value={scheduleEndDay ?? ''} onChange={(event) => setScheduleEndDay(event.target.value === '' ? null : Number(event.target.value))} className="px-3 py-2 border rounded text-sm"><option value="">Dia final</option>{DAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}</select>
                <input type="number" min={0} max={23} value={scheduleStartHour ?? ''} onChange={(event) => setScheduleStartHour(event.target.value === '' ? null : Number(event.target.value))} className="px-3 py-2 border rounded text-sm" placeholder="Hora inicial" />
                <input type="number" min={0} max={23} value={scheduleEndHour ?? ''} onChange={(event) => setScheduleEndHour(event.target.value === '' ? null : Number(event.target.value))} className="px-3 py-2 border rounded text-sm" placeholder="Hora final" />
              </div>
              <div className="flex justify-end gap-2"><button onClick={reset} className="px-4 py-2 border rounded text-sm bg-white">Cancelar</button><button onClick={save} className="px-4 py-2 rounded text-sm bg-indigo-600 text-white">Salvar</button></div>
            </div>
          )}
        </div>
      </div>

      {showDoc && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6">
            <div className="flex justify-between mb-4"><h2 className="text-lg font-semibold">Documentacao do webhook</h2><button onClick={() => setShowDoc(false)} className="text-gray-500">Fechar</button></div>
            <p className="text-sm text-gray-600 mb-3">Envie um POST JSON para a URL da automacao desejada.</p>
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">URLs dos webhooks</h3>
              {automations.length === 0 ? (
                <p className="text-xs text-gray-500">Crie uma automacao para gerar a URL do webhook.</p>
              ) : (
                <div className="space-y-2">
                  {automations.map((automation) => (
                    <div key={automation.id} className="rounded border border-gray-200 bg-white p-2">
                      <div className="mb-1 text-xs font-medium text-gray-700">{automation.name}</div>
                      <div className="flex gap-2">
                        <input readOnly value={automation.webhookUrl} className="min-w-0 flex-1 rounded border px-2 py-1 text-xs" />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(automation.webhookUrl); toast.success('URL copiada'); }} className="rounded border bg-white px-2 py-1 text-xs hover:bg-gray-50">Copiar</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <pre className="bg-slate-900 text-slate-100 rounded p-4 text-xs overflow-auto">{'{\n  "phone": "5547999999999",\n  "name": "Maria Silva",\n  "variables": {\n    "itens": "Produto A, Produto B",\n    "numeroPedido": "12345",\n    "numeroNotaFiscal": "98765",\n    "valorPedido": "199,90",\n    "statusPedido": "Entregue",\n    "produto": "Tenis Azul",\n    "codigo": "ABC-123"\n  }\n}'}</pre>
            <p className="text-xs text-gray-500 mt-3">Tambem aceita telefone, nome, contact.telefone e contact.nome. Nas mensagens use {'{{nome}}'}, {'{{telefone}}'}, {'{{itens}}'}, {'{{numeroPedido}}'}, {'{{numeroNotaFiscal}}'}, {'{{valorPedido}}'}, {'{{statusPedido}}'} ou qualquer chave dentro de variables.</p>
            <p className="text-xs text-gray-600 mt-3">Ao receber o POST, a API responde imediatamente com HTTP 202 e confirma o recebimento. O envio da mensagem continua em segundo plano.</p>
            <pre className="bg-slate-900 text-slate-100 rounded p-4 mt-2 text-xs overflow-auto">{'{\n  "success": true,\n  "received": true,\n  "status": "received",\n  "message": "Webhook recebido com sucesso",\n  "logId": "..."\n}'}</pre>
          </div>
        </div>
      )}
    </>
  );
}

